/*
 *   File : detail.js
 *   Author : https://evoqins.com
 *   Description : detail component
 *   Integrations : sub-admin/details
 *   Version : 1.0.1
 */
import React, { useEffect, useState } from "react";
import { PrimaryButton } from "../../Components/Buttons";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setPageTitle,
  setSearchEnabled,
  setNavLink,
} from "../../Redux/Ui/ui.slice";
import { Tabs } from "../../Components/Navigation";
import { Orders, Overview, Users } from "./Helper";
import { APIService } from "../../Service";
import { toast } from "react-toastify";
import { Loader } from "../../Components/Other";
import { ConfirmationModal } from "../../Components/Modals";
import { CustomDatePicker } from "../../Components/Filters";

const TAB_DATA = [
  {
    id: 0,
    label: "overview",
    name: "Overview",
  },
  {
    id: 1,
    label: "users",
    name: "Users",
  },
  {
    id: 2,
    label: "orders",
    name: "Orders",
  },
];

export default function ManageSubAdminDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [adminDetails, setAdminDetails] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [curTab, setCurTab] = useState(0);
  const [loader, setLoader] = useState(false);
  const [blockLoader, setBlockLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dateRange, setDateRange] = useState();

  useEffect(() => {
    if (id) {
      _fetchAdminDetails();
    }
    dispatch(setNavLink(["/manage-sub-admin"]));
    dispatch(setSearchEnabled(false));
  }, [id]);

  useEffect(() => {
    _fetchAdminDetailsCards();
  }, [dateRange]);

  // ---------------------------
  // FETCH ADMIN DETAILS
  // ---------------------------
  const _fetchAdminDetails = () => {
    setLoader(true);

    const payload = {
      id: parseInt(id),
    };

    APIService(true, "sub-admin/details", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const data = res.data;

          // Set admin details
          setAdminDetails(data.admin_data);

          // Set dashboard data for Overview tab
          setDashboardData(data.dashboard);

          // Set page title with admin name
          dispatch(
            setPageTitle([
              "Sub Admin",
              data.admin_data?.admin_name || "Details",
            ]),
          );
        } else {
          toast.error(res.message || "Failed to load admin details");
        }
      })
      .catch((error) => {
        console.error("Error fetching admin details:", error);
        toast.error("Failed to load admin details");
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const _fetchAdminDetailsCards = () => {
    setLoader(true);

    const payload = {
      id: parseInt(id),
      ...dateRange,
    };

    APIService(true, "sub-admin/details", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const data = res.data;
          setDashboardData(data.dashboard);
        } else {
          toast.error(res.message || "Failed to load admin details");
        }
      })
      .catch((error) => {
        console.error("Error fetching admin details:", error);
        toast.error("Failed to load admin details");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  // ---------------------------
  // BLOCK/UNBLOCK ADMIN
  // ---------------------------
  const _handleBlockToggle = () => {
    setBlockLoader(true);

    const payload = {
      id: parseInt(id),
    };

    APIService(true, "sub-admin/block", payload)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success(res.message || "Admin blocked successfully");
          setShowModal(false);
          // Refresh admin details
          _fetchAdminDetails();
        } else {
          toast.error(res.message || "Failed to block admin");
        }
      })
      .catch((error) => {
        console.error("Error blocking admin:", error);
        toast.error("Failed to block admin");
      })
      .finally(() => {
        setBlockLoader(false);
      });
  };

  // Show loader while fetching data
  if (loader) {
    return <Loader />;
  }

  // Show empty state if no data
  if (!adminDetails) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <p className="text-muted fs-18">No admin details found.</p>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column e-glass-card rounded-16 bg-transparent p-24">
          {/* Header  */}
          <div className="row">
            <div className="col-12 ">
              <div className="d-flex w-100 flex-column e-glass-card rounded-16 p-24">
                {/* Header section */}
                <div className="d-flex column-gap-2">
                  <div className="d-flex align-items-center text-eerie-black ">
                    <div className="d-flex e-liner-admin-image p-1x rounded-circle">
                      <img
                        src={adminDetails.image}
                        alt="sub admin Image"
                        width={58}
                        height={58}
                        className=" rounded-circle object-fit-cover border-facebook-blue"
                      />
                    </div>
                    <h5 className="fs-18 ps-19 lh-20 fw-500  text-capitalize">
                      {adminDetails?.admin_name}
                    </h5>
                  </div>

                  <div className="ms-auto ">
                    <div className="d-flex align-items-center justify-content-end">
                      <PrimaryButton
                        icon="block"
                        ClassName="border-copper-red bg-transparent px-3 py-2 fs-14 fw-400 text-copper-red rounded-pill"
                        size="16px"
                        name={
                          adminDetails?.status === "Active"
                            ? "Block Sub Admin"
                            : "Unblock Sub Admin"
                        }
                        onPress={() => setShowModal(true)}
                      />
                    </div>
                  </div>
                </div>
                {/* Card section */}
                <div className="d-flex pt-24 justify-content-between row-gap-2 flex-wrap">
                  {/* RM Code */}
                  <div className="d-flex h-100 flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-center">
                      <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16  ">
                        RM Code
                      </h4>
                    </div>
                    <div>
                      <p className="fw-400 fs-18 lh-24 text-eerie-black p-0 m-0">
                        {adminDetails?.rm_code || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Mail */}
                  <div className="d-flex h-100 flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-center">
                      <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16  ">
                        Mail
                      </h4>
                    </div>
                    <div>
                      <p className="fw-400 fs-18 lh-24 text-eerie-black p-0 m-0">
                        {adminDetails?.admin_email || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="d-flex h-100 flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-center">
                      <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16  ">
                        Phone no:
                      </h4>
                    </div>
                    <div>
                      <p className="fw-400 fs-18 lh-24 text-eerie-black p-0 m-0">
                        {adminDetails?.customer_phone !== "None"
                          ? adminDetails?.customer_phone
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Created on */}
                  <div className="d-flex h-100 flex-column justify-content-between">
                    <div className="d-flex justify-content-between align-items-center">
                      <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16  ">
                        Created on
                      </h4>
                    </div>
                    <div>
                      <p className="fw-400 fs-18 lh-24 text-eerie-black p-0 m-0">
                        {adminDetails?.created || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="d-flex h-100 flex-column justify-content-between pe-lg-167">
                    <div className="d-flex justify-content-between align-items-center">
                      <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16  ">
                        status
                      </h4>
                    </div>
                    <div>
                      <p
                        className={`${
                          adminDetails?.status === "Active"
                            ? "text-medium-sea-green"
                            : "text-cg-red"
                        } mb-0 p-0 fs-18 fw-400 lh-24`}
                      >
                        {adminDetails?.status || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* tab  */}
          <div className="row pt-24 pb-3">
            <div className="col-12">
              <div className="d-flex w-100  justify-content-between align-items-center">
                <Tabs
                  data={TAB_DATA}
                  defaultActive={0}
                  onChange={(activeId) => setCurTab(activeId)}
                />
                {curTab === 0 && (
                  <CustomDatePicker
                    key={"resetKey"} // increment resetKey to force remount
                    value={dateRange}
                    onChange={({ from_date, till_date }) => {
                      setDateRange({from_date, till_date});
                    }}
                    placeholder="Select date range"
                  />
                )}
              </div>
            </div>
          </div>
          {/* outlet */}
          <div className="row">
            <div className="col-12">
              {curTab === 0 && <Overview dashboardData={dashboardData} />}
              {curTab === 1 && <Users adminId={id} />}
              {curTab === 2 && <Orders adminId={id} />}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showModal}
        title={
          adminDetails?.status === "Active"
            ? "Block Sub Admin"
            : "Unblock Sub Admin"
        }
        message={
          adminDetails?.status === "Active"
            ? "Are you sure you want to block this sub admin?"
            : "Are you sure you want to unblock this sub admin?"
        }
        onConfirm={_handleBlockToggle}
        onCancel={() => setShowModal(false)}
        loading={blockLoader}
      />
    </div>
  );
}
